import { is_organization_admin_role } from "@/utils/organization_profile";
import { get_profile_url } from "@/views/home/home_helper.js";

export default {
  name: 'UserJourney',

  props: {
    features: {
      type: Object,
      default: () => ({}),
    },

    userDetails: {
      type: Object,
      default: () => ({}),
    },

    title: {
      type: String,
      default: 'Welcome to your home',
    },

    subtitle: {
      type: String,
      default:
        'There is no feed activity yet. Start with any feature available to you.',
    },
  },

  emits: ['create-post', 'create-portal', 'show-your-voice'],

  computed: {
    user_id() {
      return this.userDetails?.user_id || this.userDetails?.id || '';
    },

    portals_label() {
      return String(this.features?.portals_label || 'Portals');
    },

    portals_label_lower() {
      return this.portals_label.toLowerCase();
    },
    can_create_business_profile() {
      return this.is_enabled_feature(this.features?.show_business_profile)
        && !is_organization_admin_role(this.userDetails);
    },

    journey_cards() {
      return [
        ...this.general_cards,
        ...this.community_cards,
        ...this.profile_cards,
        ...this.organization_cards,
      ].filter(card => card.show);
    },

    general_cards() {
      return [
        {
          show: true,
          icon: 'bi-pencil-square',
          title: 'Create your first post',
          description:
            'Share an update, upload media, or start a conversation in your feed.',
          action: 'Create Post',
          event: 'create-post',
        },

        {
          show: this.features.show_library,
          icon: 'bi-bookmark',
          title: 'Use your library',
          description:
            'Save useful documents from posts and keep them ready for later.',
          action: 'Open Library',
          href: '/library',
        },

        {
          show: this.features.show_recent,
          icon: 'bi-clock-history',
          title: 'Review recent uploads',
          description:
            'Find the newest documents and resources shared in your workspace.',
          action: 'Recent Uploads',
          href: '/view_recent_uploads',
        },

        {
          show: this.features.show_faq,
          icon: 'bi-question-circle',
          title: 'Read FAQs',
          description:
            'Get quick answers and guidance for using the platform.',
          action: 'Open FAQ',
          href: '/faq',
        },
      ];
    },

    community_cards() {
      return [
        ...this.event_cards,
        ...this.incident_cards,
        ...this.community_engagement_cards,
      ];
    },
    organization_cards() {
    return [
      ...this.portal_cards,
      ...this.business_cards,
      ...this.membership_cards,
    ];
  },

    event_cards() {
    return [
      {
        show: this.features.show_events,
        icon: 'bi-calendar-event',
        title: 'Discover events',
        description: 'Browse upcoming events, RSVP, or get tickets when events are available.',
        action: 'View Events',
        href: '/view_events',
      },
      {
        show: this.features.show_rsvp,
        icon: 'bi-check2-square',
        title: 'Manage RSVPs',
        description: 'Track attendance and manage responses for events.',
        action: 'Manage RSVPs',
        href: '/manage_rsvps',
      },
    ];
  },

  incident_cards() {
    return [
      {
        show: this.features.show_maintenance,
        icon: 'bi-tools',
        title: 'Log maintenance',
        description: 'Report maintenance work and keep requests visible to the right team.',
        action: 'Report Maintenance',
        href: '/incident_reporting',
      },
      {
        show: this.features.show_incident,
        icon: 'bi-exclamation-triangle',
        title: 'Report an incident',
        description: 'Capture incidents, track progress, and keep records in one place.',
        action: 'Incident Reporting',
        href: '/incident_reporting',
      },
      {
        show: this.features.show_assets,
        icon: 'bi-box-seam',
        title: 'Explore assets',
        description: 'View and manage community assets linked to your workspace.',
        action: 'View Assets',
        href: '/community_asset',
      },
    ];
  },

  community_engagement_cards() {
    return [
      {
        show: this.features.show_upcoming_birthdays,
        icon: 'bi-gift',
        title: 'See upcoming birthdays',
        description: 'Keep track of upcoming birthdays in your community.',
        action: 'View Birthdays',
        href: '/view_all_birthdays',
      },
      {
        show: this.features.show_your_voice,
        icon: 'bi-chat-dots',
        title: 'Use Your Voice',
        description: 'Start discussions, surveys, or polls to gather input from your community.',
        action: 'Open Your Voice',
        event: 'show-your-voice',
      },
    ];
  },

    profile_cards() {
      return [
        {
          show: this.features.show_personal_profile,
          icon: 'bi-person-badge',
          title: 'Complete your profile',
          description:
            'Add your personal details and make it easier for people to recognize you.',
          action: 'View Profile',
          href: this.user_id
            ? get_profile_url({ id: this.user_id })
            : '/profile',
        },

        {
          show: this.features.show_report_author,
          icon: 'bi-person-lines-fill',
          title: 'Post as a profile',
          description:
            'Choose the author profile you want to use when sharing updates.',
          action: 'Manage Profile',
          href: this.user_id
            ? get_profile_url({ id: this.user_id })
            : '/profile',
        },
      ];
    },

  portal_cards() {
    return [
      {
        show: this.features.show_portals,
        icon: 'bi-grid-3x3-gap',
        title: `Create ${this.portals_label_lower}`,
        description: `Set up ${this.portals_label_lower} for teams, topics, publishing, and collaboration.`,
        action: `Create ${this.portals_label}`,
        event: 'create-portal',
      },
    ];
  },

  business_cards() {
    return [
      {
        show: this.features.show_ads,
        icon: 'bi-megaphone',
        title: 'Manage ads',
        description: 'Create and review promotional placements for your audience.',
        action: 'Ads Centre',
        href: '/ads_center',
      },

      {
        show: this.features.show_marketplace,
        icon: 'bi-shop',
        title: 'Browse marketplace',
        description: 'Explore products and services from profiles in your network.',
        action: 'Marketplace',
        href: '/marketplace',
      },

      {
        show: this.can_create_business_profile,
        icon: 'bi-briefcase',
        title: 'Create a business profile',
        description: 'Set up your business presence and make it visible to the community.',
        action: 'Add Business',
        modal: '#company_profile_modal',
      },
    ];
  },

  membership_cards() {
    return [
      {
        show:
          this.features.show_membership ||
          this.features.show_memberships,
        icon: 'bi-patch-check',
        title: 'Set up memberships',
        description: 'Create membership plans and manage member access.',
        action: 'Memberships',
        href: '/create_membership',
      },

      {
        show: this.features.show_dynamic_forms,
        icon: 'bi-ui-checks',
        title: 'Build dynamic forms',
        description: 'Create forms and sections to collect structured information.',
        action: 'Create Form',
        href: '/create_form/',
      },
    ];
  },
  },

  methods: {
    is_enabled_feature(value) {
      return value === true || value === 'true' || value === 1 || value === '1';
    },
    handle_card_click(card) {
      if (card.event) {
        this.$emit(card.event);
      }
    },
  },
};
